import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Expand, ZoomIn, Grid3X3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

// Helper to get full image URL (handles both relative and absolute URLs)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getImageUrl = (url) => {
  if (!url) return null;
  // If already an absolute URL, return as-is
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  // If relative path (starts with /), prepend backend URL
  if (url.startsWith('/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

export const PropertyGallery = ({ images, title, onViewAll }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  
  // Transform image URLs - prepend backend URL for relative paths
  const displayImages = images?.length > 0 
    ? images.map(img => getImageUrl(img)).filter(Boolean)
    : ['https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200'];
  
  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % displayImages.length);
  };
  
  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  const goToImage = (index) => {
    setCurrentIndex(index);
  };

  return (
    <>
      {/* Main Image Display */}
      <div className="relative aspect-video rounded-sm overflow-hidden group" data-testid="property-gallery">
        <img 
          src={displayImages[currentIndex]} 
          alt={`${title} - Image ${currentIndex + 1}`}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
        />
        
        {/* Navigation Arrows */}
        {displayImages.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid="gallery-next"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </>
        )}
        
        {/* Image Counter */}
        <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur-sm px-3 py-1.5 rounded-full text-sm text-white">
          {currentIndex + 1} / {displayImages.length}
        </div>
        
        {/* View All / Expand Button */}
        <div className="absolute bottom-4 right-4 flex gap-2">
          {displayImages.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsLightboxOpen(true)}
              className="bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm"
              data-testid="gallery-view-all"
            >
              <Grid3X3 className="w-4 h-4 mr-2" />
              View All ({displayImages.length})
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsLightboxOpen(true)}
            className="bg-black/70 hover:bg-black/90 text-white backdrop-blur-sm"
          >
            <Expand className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Thumbnail Dots - Mobile */}
        {displayImages.length > 1 && displayImages.length <= 5 && (
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 md:hidden">
            {displayImages.map((_, idx) => (
              <button
                key={idx}
                onClick={() => goToImage(idx)}
                className={`w-2 h-2 rounded-full transition-all ${
                  idx === currentIndex 
                    ? 'bg-primary w-4' 
                    : 'bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Thumbnail Strip - Desktop */}
      {displayImages.length > 1 && (
        <div className="hidden md:flex gap-2 mt-2 overflow-x-auto pb-2">
          {displayImages.slice(0, 8).map((img, idx) => (
            <button
              key={idx}
              onClick={() => goToImage(idx)}
              className={`flex-shrink-0 w-24 h-16 rounded overflow-hidden transition-all ${
                idx === currentIndex 
                  ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' 
                  : 'opacity-60 hover:opacity-100'
              }`}
              data-testid={`gallery-thumb-${idx}`}
            >
              <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
          {displayImages.length > 8 && (
            <button
              onClick={() => setIsLightboxOpen(true)}
              className="flex-shrink-0 w-24 h-16 rounded bg-secondary/50 flex items-center justify-center text-sm text-muted-foreground hover:bg-secondary/70 transition-colors"
            >
              +{displayImages.length - 8} more
            </button>
          )}
        </div>
      )}
      
      {/* Lightbox Dialog */}
      <Dialog open={isLightboxOpen} onOpenChange={setIsLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-0">
          <div className="relative h-[90vh] flex flex-col">
            {/* Close Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsLightboxOpen(false)}
              className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/80 text-white"
            >
              <X className="w-5 h-5" />
            </Button>
            
            {/* Main Lightbox Image */}
            <div className="flex-1 flex items-center justify-center p-8">
              <img 
                src={displayImages[currentIndex]} 
                alt={`${title} - Image ${currentIndex + 1}`}
                className="max-h-full max-w-full object-contain"
              />
              
              {/* Nav Arrows */}
              {displayImages.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white"
                  >
                    <ChevronLeft className="w-8 h-8" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white"
                  >
                    <ChevronRight className="w-8 h-8" />
                  </Button>
                </>
              )}
            </div>
            
            {/* Bottom Thumbnail Strip */}
            {displayImages.length > 1 && (
              <div className="h-24 bg-black/50 backdrop-blur-sm flex items-center justify-center gap-2 px-4 overflow-x-auto">
                {displayImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => goToImage(idx)}
                    className={`flex-shrink-0 w-16 h-16 rounded overflow-hidden transition-all ${
                      idx === currentIndex 
                        ? 'ring-2 ring-primary scale-110' 
                        : 'opacity-50 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            
            {/* Image Counter */}
            <div className="absolute bottom-28 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full text-white text-sm">
              {currentIndex + 1} of {displayImages.length} — {title}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PropertyGallery;
